<template>
  <View as="article" class="chat-message" :class="message.isSystem ? 'is-tallo' : 'is-renter'">
    <View justify="center" align="start" :shrink="0" class="node">
      <!-- Tallo node = the favicon: cream tile, dark t, orange dot (cream/ink sampled from
           favicon.ico — no exact tokens exist; the dot is orange-500). -->
      <View v-if="message.isSystem" as="span" align="center" justify="center" class="tallo-node" aria-hidden="true">
        <!-- The glyph stays raw markup rather than Text: it is a drawn mark, not copy, and every
             one of its metrics is off-scale. Both halves are elements, never a bare text node:
             prettier runs with htmlWhitespaceSensitivity "ignore" and will re-break this tag, and
             Vue only drops the resulting whitespace when it sits between two elements. As raw text
             the t rendered "t ." -->
        <span class="tallo-mark">
          <span>t</span>
          <em>.</em>
        </span>
      </View>
      <!-- Renter node = a neutral glyph on a soft tint keyed off the name — same geometry as the
           Tallo node so both sit on the rail identically. -->
      <View
        v-else
        as="span"
        align="center"
        justify="center"
        class="renter-node"
        :class="`tint-${renterColor}`"
        aria-hidden="true"
      >
        <Icon name="user-01" size="extra-small" class="renter-icon" />
      </View>
    </View>

    <View direction="column" :grow="1" class="entry-body">
      <View align="start" gap="sm" class="entry-head">
        <View align="center" class="head-main">
          <Text variant="label-large" as="span" class="sender">{{ senderName }}</Text>
          <Badge v-if="isFollowUp && !chip" label="Follow-up" variant="grey" size="small" class="sender" />
          <button
            v-if="chip"
            type="button"
            class="trace-chip"
            :class="`tone-${chip.tone}`"
            :aria-expanded="isTraceOpen"
            @click="isTraceOpen = !isTraceOpen"
          >
            <span ref="chipLabelRef" class="chip-label" :class="{ 'is-clipped': isChipClipped }">{{ chip.label }}</span>
            <Icon name="chevron-right" class="caret" />
          </button>
        </View>
        <Text variant="body-small" color="secondary" as="span" class="meta" :title="fullTimestamp">
          <span>{{ time }}</span>
          <span class="meta-detail">
            <span class="meta-detail-inner">{{ metaDetail }}</span>
          </span>
        </Text>
      </View>

      <ConversationTracePanel v-if="chip && isTraceOpen" :chip="chip" />

      <Text variant="body-medium" class="content">{{ message.content }}</Text>

      <ConversationMessageFeedback v-if="message.isSystem" :message-id="message.id" :feedback="message.feedback" />
    </View>
  </View>
</template>

<script setup lang="ts">
import { useDateFormat, useResizeObserver } from '@vueuse/core';
import { computed, ref } from 'vue';

import { Badge, Icon, Text, View, getAvatarColor } from '@tallo/design-system';

import { CommunicationChannel, MessageDto } from '../models/message.interface';
import { synthesizeTraceChip } from '../traces/synthesize-trace-chip.function';
import ConversationMessageFeedback from './conversation-message-feedback.vue';
import ConversationTracePanel from './conversation-trace-panel.vue';

const props = defineProps<{ message: MessageDto; timeZone?: string }>();

const senderName = computed(() => (props.message.isSystem ? 'Tallo' : props.message.user?.name || 'Renter'));

// Same function the design-system Avatar uses, so a renter wears one colour everywhere they appear
// — this node, their profile header, their lead card.
const renterColor = computed(() => getAvatarColor(senderName.value));

// Follow-up nudges are persisted with the (capitalised) backend message type 'Follow up'. The FE
// MessageType enum is lower-cased and has no follow-up member, so compare against the raw value.
// The badge only fills in for messages without traces — traced follow-ups get a chip instead.
const isFollowUp = computed(() => (props.message.type as string) === 'Follow up');

const chip = computed(() => synthesizeTraceChip(props.message.traces ?? [], props.timeZone));
const isTraceOpen = ref(false);

// The fade may only sit over text that is actually cut off: on a label that fits, the gradient
// would wash out its last word for no reason. Measured rather than inferred from hover, because
// "is there room" depends on the panel width and the label's own length, not on the pointer. The
// observer fires throughout the shrink, so the class lands on the frame the clipping begins.
const chipLabelRef = ref<HTMLElement | null>(null);
const isChipClipped = ref(false);

useResizeObserver(chipLabelRef, ([entry]) => {
  const label = entry.target as HTMLElement;
  // A pixel of slack: sub-pixel layout makes scrollWidth exceed clientWidth on labels that fit.
  isChipClipped.value = label.scrollWidth - label.clientWidth > 1;
});

const time = useDateFormat(() => props.message.createdAt, 'h:mm A');
// US-style short date: weekday first, no year — the title attribute carries the year for old threads.
const dayLabel = useDateFormat(() => props.message.createdAt, 'ddd, MMM D');
const fullTimestamp = useDateFormat(() => props.message.createdAt, 'MMM DD, YYYY h:mm A');
// Built as one string rather than interpolated pieces: inside the flex meta line, stray template
// whitespace would become its own anonymous flex item.
const metaDetail = computed(() => {
  const channel = props.message.communicationChannel;
  const channelLabel = channel === CommunicationChannel.SMS ? 'Text' : channel;

  return channelLabel ? ` · ${dayLabel.value} · ${channelLabel}` : ` · ${dayLabel.value}`;
});
</script>

<style scoped lang="scss">
.chat-message {
  // Every on-hover reveal in the entry shares this, so the meta line and the rating thumbs animate
  // as one gesture rather than two that happen to be near each other.
  --entry-reveal: 130ms ease-in-out;

  position: relative;
  // The node column is a fixed rail the avatar centres on; the body takes the rest.
  column-gap: 0.875rem;
  padding: 0.375rem 0;
  width: 100%;
}

.node {
  position: relative;
  z-index: 1;
  width: 1.75rem;
}

.tallo-node {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.5rem;
  background: #e6e0d2;
  padding-left: 0.0625rem;
  padding-top: 0.0625rem;
}

.tallo-mark {
  font-weight: 700;
  font-size: 0.8125rem;
  letter-spacing: -0.0375rem;
  line-height: 1;
  margin-top: -0.1875rem;
  color: #101411;

  em {
    font-style: normal;
    color: var(--t-color-orange-500);
  }
}

.renter-node {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.5rem;
  // The inset hairline keeps the pale tiles from dissolving into the white thread.
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 6%);

  // .svg-icon bumps specificity above Icon's own size class, which also sets width/height.
  .renter-icon.svg-icon {
    width: 0.875rem;
    height: 0.875rem;
  }

  // The avatar palette, one block per getAvatarColor value. The wash and the 700 glyph are this
  // rail's own treatment; only the hue is shared, which is what carries the recognition.
  &.tint-blue {
    background: linear-gradient(155deg, var(--t-color-blue-100), var(--t-color-blue-300));
    color: var(--t-color-blue-700);
  }

  &.tint-green {
    background: linear-gradient(155deg, var(--t-color-green-100), var(--t-color-green-300));
    color: var(--t-color-green-700);
  }

  &.tint-violet {
    background: linear-gradient(155deg, var(--t-color-violet-100), var(--t-color-violet-300));
    color: var(--t-color-violet-700);
  }

  &.tint-orange {
    background: linear-gradient(155deg, var(--t-color-orange-100), var(--t-color-orange-300));
    color: var(--t-color-orange-800);
  }

  &.tint-yellow {
    background: linear-gradient(155deg, var(--t-color-yellow-100), var(--t-color-yellow-300));
    color: var(--t-color-yellow-800);
  }
}

.entry-body {
  min-width: 0;
}

.entry-head {
  margin-bottom: 0.1875rem;
}

// nowrap is load-bearing. The chip must never drop to a second line — not at rest, and above all
// not during the 130ms the meta line spends collapsing after the pointer leaves, when the head is
// briefly under pressure with no hover state left to relieve it.
// flex-wrap stays here rather than moving to View's wrap prop: the touch media query below flips it
// back to wrap, and keeping both declarations in one place is what makes that pair readable.
.head-main {
  flex-wrap: nowrap;
  // Between the xs and sm tokens — the name, badge and chip are one phrase, not three controls.
  gap: 0.3125rem;
  // flex, not View's grow prop: the 0% basis is what stops a long chip label from sizing the
  // column before it is allowed to shrink.
  flex: 1;
  min-width: 0;
}

// Everything but the chip keeps its full width, so the chip is the only thing that gives.
.head-main .sender {
  flex: none;
}

.meta {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  flex: none;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  padding-top: 0.125rem;
}

// The day and channel trail the time and collapse to zero width at rest. The meta line is pinned to
// the right edge, so unfurling them to the right is what slides the time leftward. max-width rather
// than width: the content has no fixed size, and the ceiling only has to clear the longest label.
.meta-detail {
  display: inline-flex;
  overflow: hidden;
  max-width: 0;
  opacity: 0;
  transition:
    max-width var(--entry-reveal),
    opacity var(--entry-reveal);
}

.meta-detail-inner {
  // `pre`, not `nowrap`: this is a flex item, so its content starts a line box and a collapsible
  // leading space would be trimmed — taking the gap between the time and the separator with it.
  white-space: pre;
}

// Hover on the meta line itself (not the whole message) — the reveal answers "when was this?",
// so it belongs to the pointer resting on the time. The trigger is .meta and not the time span:
// the unfurling detail slides the time leftward out from under the pointer, and a time-only
// trigger would lose the hover mid-animation and flicker.
// :has(:focus-visible) rather than :focus-within — a mouse click on the chip leaves it focused, and
// :focus-within would hold the meta line open long after the pointer moved away. focus-visible only
// matches keyboard focus, which is exactly when the reveal is still wanted.
.meta:hover .meta-detail,
.chat-message:has(:focus-visible) .meta-detail {
  max-width: 14rem;
  opacity: 1;
}

// No hover to reveal them on touch, and a permanently expanded meta line is the noise this change
// set out to remove — so the time stands alone there.
@media (hover: none), (pointer: coarse) {
  .meta-detail {
    display: none;
  }
}

.content {
  overflow-wrap: break-word;
  max-width: 62ch;
}

.trace-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-xs);
  font-size: 0.6875rem;
  font-weight: 500;
  font-family: inherit;
  line-height: 1.5;
  padding: 0.125rem var(--t-spacing-sm);
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  background: var(--chip-tint);
  color: var(--chip-ink);
  // Permanently shrinkable, rather than only while hovered: flex takes back exactly the deficit and
  // no more, so with the meta line collapsed there is no pressure and the chip sits at its natural
  // width — the resting look is unchanged. Gating this on hover instead left the chip un-shrinkable
  // for the whole collapse animation, which is what made it flash onto a second line.
  flex: 0 1 auto;
  min-width: 0;
  transition: filter 120ms ease-in-out;

  &:hover {
    filter: brightness(0.96);
  }

  // The label is the only part that gives up width; the caret stays whole so the chip never stops
  // looking clickable. The fade is a wash of the chip's own tint, so even once it is on, it shows
  // only where there is clipped text under it — no ellipsis, nothing to hide when the label fits.
  .chip-label {
    position: relative;
    min-width: 2.5rem;
    overflow: hidden;
    white-space: nowrap;

    &::after {
      content: '';
      position: absolute;
      inset-block: 0;
      right: 0;
      width: 1.25rem;
      background: linear-gradient(to right, transparent, var(--chip-tint));
      pointer-events: none;
      opacity: 0;
      transition: opacity var(--entry-reveal);
    }

    &.is-clipped::after {
      opacity: 1;
    }
  }

  // .svg-icon bumps specificity above Icon's own size class, which also sets width/height.
  .caret.svg-icon {
    flex: none;
    width: 0.625rem;
    height: 0.625rem;
    opacity: 0.55;
    transition: transform 150ms ease-in-out;
  }

  &[aria-expanded='true'] .caret {
    transform: rotate(90deg);
  }

  &.tone-grey {
    --chip-tint: var(--t-color-grey-100);
    --chip-ink: var(--t-color-grey-700);
  }

  &.tone-green {
    --chip-tint: var(--t-color-green-100);
    --chip-ink: var(--t-color-green-700);
  }

  &.tone-orange {
    --chip-tint: var(--t-color-orange-100);
    --chip-ink: var(--t-color-orange-700);
  }

  &.tone-amber {
    --chip-tint: var(--t-color-yellow-100);
    --chip-ink: var(--t-color-yellow-700);
  }

  &.tone-blue {
    --chip-tint: var(--t-color-blue-100);
    --chip-ink: var(--t-color-blue-700);
  }

  &.tone-red {
    --chip-tint: var(--t-color-red-100);
    --chip-ink: var(--t-color-red-700);
  }
}

// Touch has no hover, so the meta line never unfurls and the head is never under pressure — leave
// it wrapping at natural width, exactly as it always did there. That also means the chip is never
// clipped on touch, so the fade above can never appear there.
@media (hover: none), (pointer: coarse) {
  .head-main {
    flex-wrap: wrap;
  }

  .trace-chip {
    flex: none;
  }
}

.chat-message :deep(.message-feedback) {
  // Balances the bar between the message it rates and the entry below. The 1.5rem buttons already
  // carry 0.25rem of their own padding around a 1rem glyph, and the entry below contributes its own
  // top padding, so the raw gaps are lopsided: roughly 2px above the glyphs against 20px below.
  // These two pull that to about 8px and 12px — still closer to its own message, as it should be.
  margin-top: 0.25rem;
  margin-bottom: -0.5rem;
  opacity: 0;
  // Arrives from the left while the meta line unfurls from the right — the entry opens from both
  // edges at once on the same token, rather than one half moving and the other blinking on. A
  // translate, not a width unfurl like the meta's: this row's height is already allocated so
  // nothing reflows, and both thumbs arrive together instead of the second being clipped until the
  // animation ends. Under a quarter of the thumb's own width, so it settles well before a pointer
  // travelling toward it could arrive.
  transform: translateX(-0.375rem);
  transition:
    opacity var(--entry-reveal),
    transform var(--entry-reveal);
}

.chat-message:hover :deep(.message-feedback),
.chat-message:has(:focus-visible) :deep(.message-feedback),
.chat-message :deep(.message-feedback.is-engaged) {
  opacity: 1;
  transform: none;
}

// Touch has no hover, so the bar simply stands at rest position, never caught mid-reveal.
@media (hover: none), (pointer: coarse) {
  .chat-message :deep(.message-feedback) {
    opacity: 1;
    transform: none;
  }
}
</style>
